import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { GeofenceSetting } from '@app-asset-config/models/geofence.model';
import { GeofenceService } from '@app-asset-config/services/geofence.service';
import { GeofenceSettingCreateComponent } from '../geofence-setting-create/geofence-setting-create.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DataService } from '@app-core/services/data/data.service';

@Component({
  selector: 'app-geofence-settings-listing',
  templateUrl: './geofence-settings-listing.component.html',
  styleUrls: ['./geofence-settings-listing.component.scss'],
})
export class GeofenceSettingsListingComponent implements OnInit, OnDestroy {
  @Input() mode: 'list' | 'select' | 'multiselect' = 'list';
  @Input() public preSelect: GeofenceSetting | number[]; // number when select mode, number[] when multiselect mode
  @Input() canSelectionEmpty: boolean = false;
  @Input() from: 'home' | 'filter' = 'home';

  @Output() selected = new EventEmitter();

  public preSelected: number[] = [];

  public geofenceSettingList: GeofenceSetting[] = [];

  public unSubscribe = new Subject();

  public defaultSetting: GeofenceSetting;

  @ViewChild('loadMoreTrigger') loadMoreTrigger!: ElementRef;
  private observer!: IntersectionObserver;

  constructor(public geofenceService: GeofenceService, public dataService: DataService) {}

  ngOnInit(): void {
    this.preSelected = Array.isArray(this.preSelect) ? [...this.preSelect] : [];

    // getting data to list geofence setting by subscribing
    this.geofenceService.geofenceSettingSubject.pipe(takeUntil(this.unSubscribe)).subscribe((val) => {
      this.geofenceSettingList = val != null ? val : [];
    });

    // getting data for default setting by subscribing
    this.geofenceService.geofenceDefaultSettingSubject.pipe(takeUntil(this.unSubscribe)).subscribe((val) => {
      this.defaultSetting = { ...val };
    });

    // triggering getting geofence list
    this.geofenceService.fetchGeofenceSetting(true);

    const subscribeForInfiniteScroll = () => {
      if (!this.loadMoreTrigger?.nativeElement) {
        return setTimeout(subscribeForInfiniteScroll, 1000);
      }

      // for infinite scroll
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.geofenceService.geofenceSkeletonLoaderSubject.getValue()) {
            this.geofenceService.fetchGeofenceSetting();
          }
        },
        { root: null }
      );

      this.observer.observe(this.loadMoreTrigger.nativeElement);
    };

    subscribeForInfiniteScroll();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.observer?.disconnect();
  }

  goBack() {
    this.geofenceService.pop();
  }

  navigateToCreateSetting() {
    this.geofenceService.navigateTo(GeofenceSettingCreateComponent, {
      notify: true,
      'output:create': (res) => {
        if (this.mode === 'select') {
          // get property Obj and set it to preSelect
          this.geofenceService.listGeofenceSetting({ propertyId: res.propertyId, skip: 0, limit: 1 }).subscribe((obj: any) => {
            if (obj.rows && obj.rows.length) {
              this.selectSetting({ ...obj.rows[0] });
              this.updateSelection();
            }
          });
        }
      },
    });
  }

  // select - mode functionalities
  selectSetting(setting: GeofenceSetting) {
    this.preSelect = setting;
  }

  // list - mode functionalities
  processRowClick(setting: GeofenceSetting, defaultSetting: boolean = false) {
    if (this.from === 'filter') {
      if (this.mode === 'select') {
        this.preSelect = { ...setting };
      }
      if (this.mode === 'multiselect') {
        // checking if the property already selected for multi select
        const checked = !this.preSelected.includes(setting.propertyId);

        this.onCheckboxChange({ checked }, setting.propertyId);
      }
    }

    if (this.from === 'home') {
      this.geofenceService.navigateTo(GeofenceSettingCreateComponent, { setting, default: defaultSetting });
    }
  }

  // multiselect - mode functionalities
  onCheckboxChange(event: any, propertyId: number) {
    if (event.checked) {
      if (Array.isArray(this.preSelected) && !this.preSelected.includes(propertyId)) {
        this.preSelected.push(propertyId);
      }
    } else {
      if (!Array.isArray(this.preSelected)) return;
      const index = this.preSelected.indexOf(propertyId);
      if (index > -1) {
        this.preSelected.splice(index, 1);
      }
    }
  }

  isChecked(propertyId: number): boolean {
    if (!Array.isArray(this.preSelected)) return;
    return this.preSelected.includes(propertyId);
  }

  updateSelection() {
    // if mode == 'select', preselect will be an object else it will be an array
    if (this.mode === 'select') {
      if (!this.preSelect) return;
      this.selected.emit(this.preSelect);

      this.goBack();
      return;
    }

    if (!this.preSelected || (Array.isArray(this.preSelected) && !this.preSelected?.length && !this.canSelectionEmpty)) {
      // no option selected
      return;
    }
    this.selected.emit(this.preSelected);

    this.goBack();
  }

  unSelectAll() {
    this.preSelected = [];
  }

  // HELPER
  isArray(value: any): boolean {
    return Array.isArray(value);
  }
}
